/**
 * Inkbond Design Studio — Canvas Editor Controller
 * Owala-inspired panel UI with Fabric.js v6
 * Text auto-adds to canvas on panel open, updates live as user types/changes settings
 */

(function () {
  'use strict';

  // Measure actual header height and set CSS variable for studio layout
  function setStudioOffset() {
    var studio = document.querySelector('.ib-studio');
    if (!studio) return;
    var rect = studio.getBoundingClientRect();
    var offset = Math.max(rect.top, 0);
    studio.style.height = 'calc(100vh - ' + offset + 'px)';
    studio.style.maxHeight = 'calc(100dvh - ' + offset + 'px)';
  }
  // Run on load, after layout settles, and on resize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setStudioOffset();
      setTimeout(setStudioOffset, 300);
    });
  } else {
    setStudioOffset();
    setTimeout(setStudioOffset, 300);
  }
  window.addEventListener('load', function () { setTimeout(setStudioOffset, 100); });
  // Only re-run on width changes — height-only resizes are the iOS keyboard
  var _lastOffsetWidth = window.innerWidth;
  window.addEventListener('resize', function () {
    if (window.innerWidth === _lastOffsetWidth) return;
    _lastOffsetWidth = window.innerWidth;
    setStudioOffset();
  });

  function waitForFabric(cb) {
    if (typeof fabric !== 'undefined') return cb();
    var checks = 0;
    var interval = setInterval(function () {
      checks++;
      if (typeof fabric !== 'undefined') { clearInterval(interval); cb(); }
      if (checks > 100) { clearInterval(interval); console.error('Fabric.js failed to load'); }
    }, 100);
  }

  // ── Canvas size modes ── (300 DPI production)
  var MODES = {
    single: {
      key: 'single',
      label: '2" × 3.5"',
      name: 'Single Tattoos',
      meta: '2" × 3.5" — cut into singles',
      hint: '2" x 3.5" — Use the tools to get started',
      sizeProp: 'Singles 2" x 3.5"',
      ratio: 3.5 / 2,          // height / width
      prodW: 600,              // 300 DPI * 2"
      prodH: 1050,             // 300 DPI * 3.5"
      widthInches: 2,
      maxImgScale: 4.0,        // raw-pixel blowup cap for images
      maxUploadDim: 2048,      // downscale cap before Worker processing
      maxW: { mobile: 360, desktop: 440 },
      // Product: "Custom Design Tattoo - Studio"
      variants: [
        { id: 54882250883363, label: '10 Pack', price: '$24.99' },
        { id: 54882250916131, label: '20 Pack (Save $10)', price: '$34.99' },
        { id: 54882250948899, label: '40 Pack (Save $30) - Ships Free', price: '$49.99' },
        { id: 54882250981667, label: '60 Pack (Save $55) - Ships Free', price: '$64.99' },
        { id: 54882251014435, label: '80 Pack (Save $85) - Ships Free', price: '$84.99' },
        { id: 54882251047203, label: '100 Pack (Save $100) - Ships Free', price: '$99.99' },
        { id: 54882251079971, label: '150 Pack (Save $170) - Ships Free', price: '$129.99' },
        { id: 54882251112739, label: '200 Pack (Save $245) - Ships Free', price: '$169.99' }
      ]
    },
    sheet: {
      key: 'sheet',
      label: '8.5" × 11"',
      name: 'Full Sheet',
      meta: '8.5" × 11" — one full sheet',
      hint: '8.5" x 11" sheet — Use the tools to get started',
      sizeProp: 'Full Sheet 8.5" x 11"',
      ratio: 11 / 8.5,
      prodW: 2550,             // 300 DPI * 8.5"
      prodH: 3300,             // 300 DPI * 11"
      widthInches: 8.5,
      maxImgScale: 6.0,        // sheets legitimately need bigger blowups
      maxUploadDim: 3000,      // keep more pixels for full-sheet printing
      maxW: { mobile: 360, desktop: 560 },
      // Product: "Custom Tattoo Sheet - Studio"
      variants: [
        { id: 55304238891299, label: '1 Sheet', price: '$24.99' },
        { id: 55304238924067, label: '2 Sheets (Save $5)', price: '$44.99' },
        { id: 55304238956835, label: '3 Sheets (Save $15)', price: '$59.99' },
        { id: 55304238989603, label: '5 Sheets (Save $35) - Ships Free', price: '$89.99' },
        { id: 55304239022371, label: '10 Sheets (Save $100) - Ships Free', price: '$149.99' }
      ]
    }
  };
  var WORKER_URL = 'https://inkbond-studio.inkbond-studio.workers.dev';
  // Mobile/desktop layout breakpoint — MUST match the CSS media queries (1023/1024)
  var MOBILE_MAX = 1023;
  // Custom props preserved through undo/autosave serialization.
  // NOTE: Fabric v6 toJSON() takes NO arguments — canvas.toObject(props) must be used instead.
  var SERIALIZE_PROPS = ['_isGuide', '_isSnapLine', 'lockUniScaling', '_curveAmount'];
  var IS_TOUCH = 'ontouchstart' in window || (window.matchMedia && window.matchMedia('(pointer: coarse)').matches);

  // ── Studio Class ──
  function InkbondStudio(opts) {
    opts = opts || {};
    this.mode = MODES[opts.mode] || MODES.single;
    this.canvas = null;
    this.history = [];
    this.historyIndex = -1;
    this.maxHistory = 50;
    this.isLoadingState = false;
    this.previewDataUrl = null;
    this.selectedColor = '#000000';
    this.selectedFont = 'Arial';
    this.selectedSize = 24;
    this.selectedStroke = null;
    this.selectedStrokeWidth = 0;
    this.selectedWeight = 'normal';
    this.selectedStyle = 'normal';

    // The "live" text object currently being edited via the text panel
    this.activeTextObj = null;

    this.init(!!opts.restore);
  }

  InkbondStudio.prototype.init = function (restore) {
    var self = this;
    this._lastLayoutWidth = window.innerWidth;
    var size = this.getCanvasSize();

    this.canvas = new fabric.Canvas('ib-studio-canvas', {
      width: size.width,
      height: size.height,
      backgroundColor: null,
      selection: true,
      preserveObjectStacking: true
    });

    this.drawGuides();
    this.saveState();
    this.bindEvents();
    // Center snapping removed per user request
    this._setupPinchZoom();
    this._syncToolbarHeight();
    this._syncModeUI();
    if (restore) this._restoreFromSave();
    this._setupAutoSave();
    this._setupLeaveWarning();
    this._setupDoubleTapEdit();
    this._setupKeyboardLift();

    // Debounced, keyboard-immune resize: iOS keyboard fires height-only resizes
    // that used to rescale/drift the whole design mid-edit.
    var resizeTimer = null;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { self.handleResize(); self._syncToolbarHeight(); }, 150);
    });

    // Re-render once all web fonts finish loading so restored/new text
    // measured against a fallback font gets correct glyph metrics.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        try { if (fabric.cache && fabric.cache.clearFontCache) fabric.cache.clearFontCache(); } catch (e) {}
        self.canvas.requestRenderAll();
      });
    }

    // Keyboard shortcuts
    document.addEventListener('keydown', function (e) {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      var k = (e.key || '').toLowerCase();
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (self.canvas.getActiveObject() && self.canvas.getActiveObject().isEditing) return;
        e.preventDefault();
        self.deleteSelected();
      }
      if (e.key === 'Escape') {
        self.canvas.discardActiveObject();
        self.canvas.renderAll();
      }
      // Case-insensitive so Shift/Caps Lock don't kill the shortcuts
      if ((e.ctrlKey || e.metaKey) && k === 'z') {
        e.preventDefault();
        if (e.shiftKey) { self.redo(); } else { self.undo(); }
      }
      if ((e.ctrlKey || e.metaKey) && k === 'y') {
        e.preventDefault();
        self.redo();
      }
      if ((e.ctrlKey || e.metaKey) && k === 'd') {
        e.preventDefault();
        self.duplicateSelected();
      }
      // Arrow-key nudge: 1px, Shift = 10px
      if (k === 'arrowup' || k === 'arrowdown' || k === 'arrowleft' || k === 'arrowright') {
        var obj = self.canvas.getActiveObject();
        if (obj && !obj._isGuide && !obj.isEditing) {
          e.preventDefault();
          var d = e.shiftKey ? 10 : 1;
          if (k === 'arrowleft') obj.left -= d;
          if (k === 'arrowright') obj.left += d;
          if (k === 'arrowup') obj.top -= d;
          if (k === 'arrowdown') obj.top += d;
          obj.setCoords();
          self.canvas.renderAll();
          clearTimeout(self._nudgeTimer);
          self._nudgeTimer = setTimeout(function () { self.saveState(); }, 400);
        }
      }
    });

    console.log('Inkbond Studio initialized', this.mode.key, size.width + 'x' + size.height);
  };

  // Push the active mode's labels/variants into the Alpine UI state
  InkbondStudio.prototype._syncModeUI = function () {
    var m = this.mode;
    this._setAlpineData('canvasMode', m.key);
    this._setAlpineData('modeLabel', m.label);
    this._setAlpineData('modeMeta', m.meta);
    this._setAlpineData('modeHint', m.hint);
    this._setAlpineData('variantList', m.variants.map(function (v) { return { id: String(v.id), label: v.label, price: v.price }; }));
    this._setAlpineData('selectedVariant', String(m.variants[0].id));
    this._setAlpineData('selectedPrice', m.variants[0].price);
    this._setAlpineData('exportWarning', '');
  };

  InkbondStudio.prototype.getCanvasSize = function () {
    var ratio = this.mode.ratio;
    var area = document.querySelector('.ib-studio__canvas-area');
    if (!area) return { width: 400, height: Math.round(400 * ratio) };

    var isMobile = window.innerWidth <= MOBILE_MAX;
    var areaRect = area.getBoundingClientRect();

    var maxWidth, maxHeight;

    if (isMobile) {
      // On mobile: subtract toolbar width (48px) + padding (12px each side)
      var toolbarEl = area.querySelector('.ib-studio__canvas-toolbar');
      var toolbarW = toolbarEl ? toolbarEl.getBoundingClientRect().width + 12 : 56;
      maxWidth = areaRect.width - toolbarW - 16;

      // Calculate available height: viewport minus everything above and below
      var studioEl = document.querySelector('.ib-studio');
      var headerEl = document.querySelector('header.header, .header');
      var headerH = headerEl ? headerEl.getBoundingClientRect().height : 0;
      var studioHeader = document.querySelector('.ib-studio__header');
      var studioHeaderH = studioHeader ? studioHeader.getBoundingClientRect().height : 0;
      // Bottom: tool tabs (~60px) + cart bar (~60px) + padding
      var bottomH = 140;
      maxHeight = window.innerHeight - headerH - studioHeaderH - bottomH - 32;
      // Cap canvas height so bottom isn't cut off
      maxHeight = Math.min(maxHeight, window.innerHeight * 0.55);
    } else {
      maxWidth = areaRect.width - 24;
      maxHeight = areaRect.height - 24;
    }

    var width = Math.min(maxWidth, maxHeight / ratio);
    width = Math.min(width, isMobile ? this.mode.maxW.mobile : this.mode.maxW.desktop);
    width = Math.max(width, 180);
    var height = Math.round(width * ratio);

    if (height > maxHeight) {
      height = maxHeight;
      width = Math.round(height / ratio);
    }

    return { width: Math.round(width), height: Math.round(height) };
  };

  InkbondStudio.prototype.handleResize = function (force) {
    // iOS keyboard immunity: the on-screen keyboard fires height-only resize
    // events. Rescaling the canvas for those shrinks/drifts the design while
    // typing, so only re-layout on real width/orientation changes.
    if (!force) {
      if (window.innerWidth === this._lastLayoutWidth) return;
      var ae = document.activeElement;
      if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA')) return;
      var active = this.canvas.getActiveObject();
      if (active && active.isEditing) return;
    }
    this._lastLayoutWidth = window.innerWidth;

    var size = this.getCanvasSize();
    var scaleX = size.width / this.canvas.getWidth();
    var scaleY = size.height / this.canvas.getHeight();

    this.canvas.setDimensions({ width: size.width, height: size.height });

    this.canvas.getObjects().forEach(function (obj) {
      if (obj._isGuide) return;
      obj.scaleX = obj.scaleX * scaleX;
      obj.scaleY = obj.scaleY * scaleY;
      obj.left = obj.left * scaleX;
      obj.top = obj.top * scaleY;
      obj.setCoords();
    });

    this.drawGuides();
    this.canvas.renderAll();
  };

  InkbondStudio.prototype.drawGuides = function () {
    // Remove any existing guides
    var self = this;
    this.canvas.getObjects().forEach(function (obj) {
      if (obj._isGuide && !obj._isSnapLine) self.canvas.remove(obj);
    });
    // No dashed border — full canvas is the print area (bleed handled in production)
  };

  // ── Center Snapping (like Canva) with hysteresis to prevent wiggle ──
  InkbondStudio.prototype._setupSnapping = function () {
    var self = this;
    var SNAP_IN = 8;    // pixels to trigger snap
    var SNAP_OUT = 20;  // pixels to release snap (must be larger than SNAP_IN)
    var isSnappedV = false;
    var isSnappedH = false;
    var snapOffsetV = 0; // difference between mouse position and snapped position
    var snapOffsetH = 0;
    var snapLineH = null;
    var snapLineV = null;

    function getSnapLines() {
      if (!snapLineH) {
        snapLineH = new fabric.Line([0, 0, 0, 0], {
          stroke: '#ff4081', strokeWidth: 1, strokeDashArray: [3, 3],
          selectable: false, evented: false, excludeFromExport: true
        });
        snapLineH._isSnapLine = true;
        snapLineH._isGuide = true;
      }
      if (!snapLineV) {
        snapLineV = new fabric.Line([0, 0, 0, 0], {
          stroke: '#ff4081', strokeWidth: 1, strokeDashArray: [3, 3],
          selectable: false, evented: false, excludeFromExport: true
        });
        snapLineV._isSnapLine = true;
        snapLineV._isGuide = true;
      }
      return { h: snapLineH, v: snapLineV };
    }

    // Reset snap state when object is released
    self.canvas.on('mouse:up', function () {
      isSnappedV = false;
      isSnappedH = false;
      snapOffsetV = 0;
      snapOffsetH = 0;
    });

    self.canvas.on('object:moving', function (e) {
      var obj = e.target;
      if (!obj || obj._isGuide) return;

      var canvasW = self.canvas.getWidth();
      var canvasH = self.canvas.getHeight();
      var centerX = canvasW / 2;
      var centerY = canvasH / 2;

      var bound = obj.getBoundingRect(true);
      var objCenterX = bound.left + bound.width / 2;
      var objCenterY = bound.top + bound.height / 2;

      var lines = getSnapLines();
      var distV = Math.abs(objCenterX - centerX);
      var distH = Math.abs(objCenterY - centerY);

      var snappedV = false;
      var snappedH = false;

      // Vertical center snap (left/right axis)
      if (isSnappedV) {
        // Already snapped — only release if dragged far enough away
        if (distV > SNAP_OUT) {
          isSnappedV = false;
          snapOffsetV = 0;
        } else {
          // Stay snapped — keep object at center
          obj.left = obj.left + (centerX - objCenterX);
          snappedV = true;
        }
      } else {
        // Not snapped — check if close enough to snap in
        if (distV < SNAP_IN) {
          obj.left = obj.left + (centerX - objCenterX);
          isSnappedV = true;
          snappedV = true;
        }
      }

      // Horizontal center snap (up/down axis)
      if (isSnappedH) {
        if (distH > SNAP_OUT) {
          isSnappedH = false;
          snapOffsetH = 0;
        } else {
          obj.top = obj.top + (centerY - objCenterY);
          snappedH = true;
        }
      } else {
        if (distH < SNAP_IN) {
          obj.top = obj.top + (centerY - objCenterY);
          isSnappedH = true;
          snappedH = true;
        }
      }

      // Show/hide snap lines
      if (snappedV) {
        lines.v.set({ x1: centerX, y1: 0, x2: centerX, y2: canvasH, visible: true });
        if (!self.canvas.getObjects().includes(lines.v)) self.canvas.add(lines.v);
      } else {
        lines.v.set('visible', false);
      }
      if (snappedH) {
        lines.h.set({ x1: 0, y1: centerY, x2: canvasW, y2: centerY, visible: true });
        if (!self.canvas.getObjects().includes(lines.h)) self.canvas.add(lines.h);
      } else {
        lines.h.set('visible', false);
      }
      if (snappedV || snappedH) obj.setCoords();

      self.canvas.renderAll();
    });

    self.canvas.on('object:modified', function () {
      // Hide snap lines after drop
      if (snapLineH) snapLineH.set('visible', false);
      if (snapLineV) snapLineV.set('visible', false);
      self.canvas.renderAll();
    });
  };

  // ── State / Undo / Redo ──
  // Serialize the canvas with custom props. Fabric v6's toJSON() takes NO
  // arguments (it drops the props list silently) — toObject() must be used.
  InkbondStudio.prototype._serializeCanvas = function () {
    return this.canvas.toObject(SERIALIZE_PROPS);
  };

  InkbondStudio.prototype.saveState = function () {
    if (this.isLoadingState) return;
    var json = this._serializeCanvas();
    this.history.splice(this.historyIndex + 1);
    this.history.push(JSON.stringify(json));
    if (this.history.length > this.maxHistory) this.history.shift();
    this.historyIndex = this.history.length - 1;
    this._syncUndoRedoUI();
  };

  InkbondStudio.prototype._syncUndoRedoUI = function () {
    this._setAlpineData('canUndo', this.historyIndex > 0);
    this._setAlpineData('canRedo', this.historyIndex < this.history.length - 1);
  };

  InkbondStudio.prototype.loadState = function (json, onDone) {
    var self = this;
    this.isLoadingState = true;

    function afterLoad() {
      if (!self.isLoadingState) return; // prevent double-call
      self.canvas.getObjects().forEach(function (obj) {
        if (obj._isGuide) {
          obj.set({ selectable: false, evented: false, excludeFromExport: true });
        }
        // Re-apply corner-only controls to images AND text (both are created locked)
        if ((obj.type === 'image' || obj.type === 'i-text' || obj.type === 'textbox') && obj.lockUniScaling) {
          self._lockImageControls(obj);
        }
        // On touch devices the panel is the only text-editing surface
        if (IS_TOUCH && (obj.type === 'i-text' || obj.type === 'textbox')) {
          obj.set('editable', false);
        }
      });
      self.canvas.renderAll();
      self.isLoadingState = false;
      self.activeTextObj = null;
      self.updateHasObjects();
      self.refreshLayers();
      if (typeof onDone === 'function') onDone();
    }

    var result = this.canvas.loadFromJSON(json);
    // Fabric.js v6 returns a Promise
    if (result && typeof result.then === 'function') {
      result.then(function () { afterLoad(); }).catch(function (err) {
        console.error('loadFromJSON error:', err);
        self.isLoadingState = false;
      });
    } else {
      // Fallback for v5-style (synchronous or callback)
      afterLoad();
    }
  };

  InkbondStudio.prototype.undo = function () {
    if (this.historyIndex <= 0 || this.isLoadingState) return;
    this.historyIndex--;
    this.loadState(this.history[this.historyIndex]);
    this._syncUndoRedoUI();
  };

  InkbondStudio.prototype.redo = function () {
    if (this.historyIndex >= this.history.length - 1 || this.isLoadingState) return;
    this.historyIndex++;
    this.loadState(this.history[this.historyIndex]);
    this._syncUndoRedoUI();
  };

  // ── Text Panel: preview mode (no auto-add) ──
  InkbondStudio.prototype.openTextPanel = function () {
    // If a text object is selected on canvas, adopt it for editing instead of
    // resetting — clicking "Text" after selecting a text means "edit this one".
    var sel = this.canvas.getActiveObject();
    if (sel && !sel._isGuide && (sel.type === 'i-text' || sel.type === 'textbox')) {
      this.adoptTextObject(sel);
      return;
    }

    // New-text mode: deselect whatever is on canvas so style taps can't
    // silently restyle a still-selected object.
    this.canvas.discardActiveObject();
    this.canvas.renderAll();
    this.activeTextObj = null;
    this._setAlpineData('textEditing', false);

    // Reset internal state FIRST (the preview renders from it)
    this.selectedColor = '#000000';
    this.selectedFont = 'Arial';
    this.selectedSize = 24;
    this.selectedStroke = null;
    this.selectedStrokeWidth = 0;
    this.selectedWeight = 'normal';
    this.selectedStyle = 'normal';

    // Reset text inputs
    var inputs = document.querySelectorAll('.js-studio-text-input');
    inputs.forEach(function (inp) { inp.value = ''; });

    // Reset the visible panel controls to match the reset state
    this._resetPanelControls();

    // Reset preview (after state reset so it renders defaults)
    this.updatePreview('Your text');
  };

  // Adopt an existing canvas text into the panel (shared by pill-click,
  // double-click, and mobile double-tap)
  InkbondStudio.prototype.adoptTextObject = function (obj) {
    this.activeTextObj = obj;
    this.selectedColor = obj.fill || '#000000';
    this.selectedFont = obj.fontFamily || 'Arial';
    this.selectedSize = obj.fontSize || 24;
    this.selectedStroke = obj.stroke || null;
    this.selectedStrokeWidth = obj.strokeWidth || 0;
    this.selectedWeight = obj.fontWeight || 'normal';
    this.selectedStyle = obj.fontStyle || 'normal';

    this._setAlpineData('activePanel', 'text');
    this._setAlpineData('textEditing', true);

    var inputs = document.querySelectorAll('.js-studio-text-input');
    inputs.forEach(function (inp) { inp.value = obj.text || ''; });

    this.updatePreview(obj.text);
    this.syncPanelToSelection({ selected: [obj] });
  };

  // Reset every visible text-panel control (desktop card + mobile sheet copies)
  // to the defaults, so the UI can't claim settings the next Add won't use.
  InkbondStudio.prototype._resetPanelControls = function () {
    // Fill swatches: only #000000 active
    document.querySelectorAll('.ib-studio__swatch:not(.js-studio-outline-swatch):not(.ib-studio__swatch--picker)').forEach(function (s) {
      s.classList.toggle('active', (s.getAttribute('data-color') || '').toUpperCase() === '#000000');
    });
    // Custom picker labels back to neutral
    document.querySelectorAll('.ib-studio__swatch--picker').forEach(function (p) { p.style.background = ''; });
    // Font cards: Arial active
    document.querySelectorAll('.ib-studio__font-card').forEach(function (b) {
      b.classList.toggle('active', b.getAttribute('data-font') === 'Arial');
    });
    // Size sliders back to 24
    document.querySelectorAll('.js-studio-size-slider').forEach(function (sl) { sl.value = 24; });
    document.querySelectorAll('.js-studio-size-value').forEach(function (sv) { sv.textContent = '24px'; });
    // Bold / Italic off
    document.querySelectorAll('.js-studio-bold, .js-studio-italic').forEach(function (b) { b.classList.remove('active'); });
    // Outline: OFF swatch active, thickness 0
    document.querySelectorAll('.js-studio-outline-swatch').forEach(function (s) {
      s.classList.toggle('active', !s.getAttribute('data-outline-color'));
    });
    document.querySelectorAll('.js-studio-outline-slider').forEach(function (sl) { sl.value = 0; });
    document.querySelectorAll('.js-studio-outline-value').forEach(function (sv) { sv.textContent = '0px'; });
    // Curve back to 0
    document.querySelectorAll('.js-studio-curve-slider').forEach(function (sl) { sl.value = 0; });
    document.querySelectorAll('.js-studio-curve-value').forEach(function (sv) { sv.textContent = '0'; });
  };

  // Ensure a web font is actually loaded before applying it to the canvas —
  // otherwise Fabric renders the fallback font and caches wrong glyph metrics.
  InkbondStudio.prototype._withFont = function (font, cb) {
    if (document.fonts && document.fonts.load) {
      document.fonts.load('16px "' + font + '"').then(function () {
        try { if (fabric.cache && fabric.cache.clearFontCache) fabric.cache.clearFontCache(font); } catch (e) {}
        cb();
      }).catch(function () { cb(); });
    } else {
      cb();
    }
  };

  // Add text to canvas from the preview panel
  InkbondStudio.prototype.addTextToCanvas = function () {
    var self = this;
    var inputs = document.querySelectorAll('.js-studio-text-input');
    var val = (inputs[0] ? inputs[0].value : '') || 'Your text';

    // Offset each new text so they don't stack
    var textCount = this.canvas.getObjects().filter(function (o) { return (o.type === 'i-text' || o.type === 'text') && !o._isGuide; }).length;
    var offset = textCount * 20;
    var textOpts = {
      left: this.canvas.getWidth() / 2 + offset,
      top: this.canvas.getHeight() / 3 + offset,
      originX: 'center',
      originY: 'center',
      fontSize: this.selectedSize,
      fontFamily: this.selectedFont,
      fill: this.selectedColor || '#000000',
      fontWeight: this.selectedWeight || 'normal',
      fontStyle: this.selectedStyle || 'normal',
      paintFirst: 'stroke',
      lockUniScaling: true,
      stroke: null,
      strokeWidth: 0
    };
    if (IS_TOUCH) textOpts.editable = false; // panel is the only editing surface on touch
    if (this.selectedStroke && this.selectedStrokeWidth > 0) {
      textOpts.stroke = this.selectedStroke;
      textOpts.strokeWidth = this.selectedStrokeWidth;
    }
    // Always deselect any existing object first
    this.canvas.discardActiveObject();
    this.activeTextObj = null;

    this._withFont(textOpts.fontFamily, function () {
      var text = new fabric.IText(val, textOpts);
      self._lockImageControls(text);
      self.canvas.add(text);
      self.canvas.renderAll();
      self.updateHasObjects();
      self.saveState();

      // Commit-and-release: the added text is DONE. Deselect it and clear the
      // input so the next keystrokes start a fresh text instead of silently
      // renaming the one just added.
      self.canvas.discardActiveObject();
      self.canvas.renderAll();
      self.activeTextObj = null;
      self._setAlpineData('textEditing', false);
      inputs.forEach(function (inp) { inp.value = ''; });
      self.updatePreview('Your text');
    });
  };

  // Update the text preview div (not canvas)
  InkbondStudio.prototype.updatePreview = function (text) {
    var self = this;
    if (!text) {
      var inputs = document.querySelectorAll('.js-studio-text-input');
      text = (inputs[0] ? inputs[0].value : '') || 'Your text';
    }
    var previews = document.querySelectorAll('.js-studio-text-preview-text');
    previews.forEach(function (el) {
      el.textContent = text;
      el.style.fontFamily = self.selectedFont;
      el.style.color = self.selectedColor || '#000000';
      el.style.fontSize = Math.min(self.selectedSize, 32) + 'px';
      el.style.fontWeight = self.selectedWeight || 'normal';
      el.style.fontStyle = self.selectedStyle || 'normal';
      // Render outline via CSS text-stroke
      if (self.selectedStroke && self.selectedStrokeWidth > 0) {
        var sw = Math.min(self.selectedStrokeWidth, 5); // cap for preview
        el.style.webkitTextStroke = sw + 'px ' + self.selectedStroke;
        el.style.paintOrder = 'stroke fill';
      } else {
        el.style.webkitTextStroke = '';
        el.style.paintOrder = '';
      }
    });
  };

  // Live update: update canvas if editing, preview if creating
  InkbondStudio.prototype.updateLiveText = function (val) {
    if (this.activeTextObj && this.canvas.getObjects().indexOf(this.activeTextObj) !== -1) {
      // Editing existing text on canvas
      this.activeTextObj.set('text', val || 'Your text');
      this.canvas.renderAll();
    }
    // Always update preview
    this.updatePreview(val);
  };

  // ── Center Object ──
  InkbondStudio.prototype.centerObject = function (axis) {
    var obj = this.canvas.getActiveObject();
    if (!obj || obj._isGuide) return;
    if (axis === 'h') {
      obj.set('left', this.canvas.getWidth() / 2);
      obj.set('originX', 'center');
    } else if (axis === 'v') {
      obj.set('top', this.canvas.getHeight() / 2);
      obj.set('originY', 'center');
    }
    obj.setCoords();
    this.canvas.renderAll();
    this.saveState();
  };

  // ── Curve Text ──
  InkbondStudio.prototype.curveText = function (amount, commit) {
    var obj = this._getTextTarget();
    if (!obj) return;

    obj._curveAmount = amount; // remembered so panels can prefill on re-edit

    if (amount === 0) {
      // Remove curve - set path to null
      obj.set('path', null);
      this.canvas.renderAll();
      if (commit !== false) this.saveState();
      return;
    }

    // Generate an SVG arc path for the curve
    var w = obj.width || 200;
    // Radius derived from curve amount: higher = tighter curve
    var radius = Math.max(50, (5000 / Math.abs(amount)));
    var sweep = amount > 0 ? 1 : 0;
    var pathStr = 'M 0 0 A ' + radius + ' ' + radius + ' 0 0 ' + sweep + ' ' + w + ' 0';

    try {
      var pathObj = new fabric.Path(pathStr, { visible: false });
      obj.set('path', pathObj);
      this.canvas.renderAll();
      if (commit !== false) this.saveState();
    } catch (e) {
      console.warn('Curve text error:', e);
    }
  };

  // ── Layers ──
  InkbondStudio.prototype.refreshLayers = function () {
    var self = this;
    var list = document.getElementById('ib-studio-layers-list');
    if (!list) return;

    var objects = this.canvas.getObjects().filter(function (obj) { return !obj._isGuide; });
    var active = this.canvas.getActiveObject();

    list.innerHTML = '';
    // Reverse order: top layer first
    for (var i = objects.length - 1; i >= 0; i--) {
      var obj = objects[i];
      var name = obj.type === 'i-text' || obj.type === 'textbox'
        ? '"' + (obj.text || '').substring(0, 16) + '"'
        : obj.type === 'image' ? 'Image' : 'Shape';

      var div = document.createElement('div');
      div.className = 'ib-studio__layer-item' + (obj === active ? ' active' : '');
      div.setAttribute('draggable', 'true');
      div.setAttribute('data-layer-idx', i);
      div.innerHTML = '<span class="ib-studio__layer-grip" title="Drag to reorder">⠿</span>' +
        '<span class="ib-studio__layer-item-name">' + name + '</span>' +
        '<span class="ib-studio__layer-arrows">' +
          '<button class="ib-studio__layer-arrow-btn js-layer-up" title="Move up">▲</button>' +
          '<button class="ib-studio__layer-arrow-btn js-layer-down" title="Move down">▼</button>' +
        '</span>';

      (function (fabricObj, idx) {
        div.addEventListener('click', function (e) {
          if (e.target.classList.contains('ib-studio__layer-grip')) return;
          if (e.target.classList.contains('ib-studio__layer-arrow-btn') || e.target.classList.contains('js-layer-up') || e.target.classList.contains('js-layer-down')) return;
          self.canvas.setActiveObject(fabricObj);
          self.canvas.renderAll();
          self.refreshLayers();
        });
        // Up/down arrow buttons
        div.querySelector('.js-layer-up').addEventListener('click', function (e) {
          e.stopPropagation();
          var allObjs = self.canvas.getObjects();
          var canvasIdx = allObjs.indexOf(fabricObj);
          if (canvasIdx < allObjs.length - 1) {
            self.canvas.moveObjectTo(fabricObj, canvasIdx + 1);
            self.canvas.renderAll();
            self.saveState();
            self.refreshLayers();
          }
        });
        div.querySelector('.js-layer-down').addEventListener('click', function (e) {
          e.stopPropagation();
          var allObjs = self.canvas.getObjects();
          var canvasIdx = allObjs.indexOf(fabricObj);
          var firstNonGuide = 0;
          for (var g = 0; g < allObjs.length; g++) { if (!allObjs[g]._isGuide) { firstNonGuide = g; break; } }
          if (canvasIdx > firstNonGuide) {
            self.canvas.moveObjectTo(fabricObj, canvasIdx - 1);
            self.canvas.renderAll();
            self.saveState();
            self.refreshLayers();
          }
        });
        // Drag & drop reordering
        div.addEventListener('dragstart', function (e) {
          e.dataTransfer.setData('text/plain', idx);
          div.style.opacity = '0.5';
        });
        div.addEventListener('dragend', function () { div.style.opacity = '1'; });
        div.addEventListener('dragover', function (e) { e.preventDefault(); div.style.background = '#e3f2fd'; });
        div.addEventListener('dragleave', function () { div.style.background = ''; });
        div.addEventListener('drop', function (e) {
          e.preventDefault();
          div.style.background = '';
          var fromIdx = parseInt(e.dataTransfer.getData('text/plain'), 10);
          var toIdx = idx;
          if (fromIdx === toIdx) return;
          // Reorder in canvas — convert reversed layer indices to canvas indices
          var nonGuides = self.canvas.getObjects().filter(function (o) { return !o._isGuide; });
          var fromObj = nonGuides[fromIdx];
          var toObj = nonGuides[toIdx];
          if (!fromObj || !toObj) return;
          var fromCanvasIdx = self.canvas.getObjects().indexOf(fromObj);
          var toCanvasIdx = self.canvas.getObjects().indexOf(toObj);
          self.canvas.moveObjectTo(fromObj, toCanvasIdx);
          self.canvas.renderAll();
          self.saveState();
          self.refreshLayers();
        });
      })(obj, i);

      list.appendChild(div);
    }
  };

  // ── Toast (inline feedback instead of alert() dialogs) ──
  InkbondStudio.prototype.toast = function (msg, kind) {
    var area = document.querySelector('.ib-studio__canvas-area');
    if (!area) { alert(msg); return; }
    var el = document.createElement('div');
    el.className = 'ib-studio__toast' + (kind === 'error' ? ' ib-studio__toast--error' : '');
    el.textContent = msg;
    area.appendChild(el);
    setTimeout(function () { el.classList.add('show'); }, 30);
    setTimeout(function () {
      el.classList.remove('show');
      setTimeout(function () { if (el.parentElement) el.remove(); }, 350);
    }, kind === 'error' ? 4500 : 2800);
  };

  // Show pinch-to-zoom hint (once per session)
  InkbondStudio.prototype._showPinchHint = function () {
    if (this._pinchHintShown) return;
    // Only show on touch devices
    if (!('ontouchstart' in window)) return;
    this._pinchHintShown = true;
    var hint = document.createElement('div');
    hint.className = 'ib-studio__pinch-hint';
    hint.textContent = '\u{1F90F} Pinch to resize';
    var canvasArea = document.querySelector('.ib-studio__canvas-area');
    if (canvasArea) {
      canvasArea.appendChild(hint);
      setTimeout(function () { hint.classList.add('show'); }, 50);
      setTimeout(function () {
        hint.classList.remove('show');
        setTimeout(function () { if (hint.parentElement) hint.remove(); }, 500);
      }, 4000);
    }
  };

  // ── 1. Auto-save to localStorage (per size mode) ──
  var AUTOSAVE_LEGACY_KEY = 'inkbond_studio_autosave';
  function autosaveKey(modeKey) { return AUTOSAVE_LEGACY_KEY + '_' + modeKey; }

  // Read a saved design payload for a mode. Handles both the v2 wrapper and
  // the legacy raw-canvas-JSON format (treated as a 'single' design).
  function readSavedPayload(modeKey) {
    try {
      var raw = localStorage.getItem(autosaveKey(modeKey));
      if (!raw && modeKey === 'single') raw = localStorage.getItem(AUTOSAVE_LEGACY_KEY);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (data && data.v === 2 && data.json) return data;
      if (data && data.objects) {
        var hasContent = data.objects.some(function (o) { return !o._isGuide && !o._isSnapLine; });
        if (!hasContent) return null;
        return { v: 1, mode: 'single', json: data, canvasW: null, canvasH: null, ts: null, thumb: null };
      }
      return null;
    } catch (e) { return null; }
  }
  // Exposed for the start screen (continue-your-design card)
  window.ibStudioSaved = function (modeKey) { return readSavedPayload(modeKey); };

  InkbondStudio.prototype._autoSaveNow = function () {
    var objs = this.canvas.getObjects().filter(function (o) { return !o._isGuide && !o._isSnapLine; });
    if (objs.length === 0) return; // NEVER auto-delete a saved design on empty canvas
    try {
      var thumb = null;
      try {
        // White background for the thumbnail — a transparent canvas encodes
        // to a black JPEG
        var origBg = this.canvas.backgroundColor;
        this.canvas.backgroundColor = '#ffffff';
        thumb = this.canvas.toDataURL({ format: 'jpeg', quality: 0.6, multiplier: 120 / this.canvas.getWidth() });
        this.canvas.backgroundColor = origBg;
      } catch (e) {}
      var payload = {
        v: 2,
        mode: this.mode.key,
        ts: Date.now(),
        canvasW: this.canvas.getWidth(),
        canvasH: this.canvas.getHeight(),
        thumb: thumb,
        json: this._serializeCanvas()
      };
      localStorage.setItem(autosaveKey(this.mode.key), JSON.stringify(payload));
      if (this.mode.key === 'single') localStorage.removeItem(AUTOSAVE_LEGACY_KEY); // migrate legacy
    } catch (e) { /* storage full or private mode */ }
  };

  InkbondStudio.prototype._setupAutoSave = function () {
    var self = this;
    // Save every 5 seconds if there are objects. An empty canvas leaves the
    // stored design untouched (deleting it here destroyed designs 5s after
    // load whenever the user hadn't restored yet).
    this._autoSaveInterval = setInterval(function () { self._autoSaveNow(); }, 5000);
  };

  // Restore this mode's saved design (called from init when the start screen's
  // "Continue your design" card was chosen)
  InkbondStudio.prototype._restoreFromSave = function () {
    var self = this;
    var payload = readSavedPayload(this.mode.key);
    if (!payload) return;
    var savedW = payload.canvasW;
    this.loadState(payload.json, function () {
      // Rescale from the canvas size the design was saved on to the current
      // one (same ratio per mode, so a uniform factor keeps everything true)
      var curW = self.canvas.getWidth();
      if (savedW && Math.abs(savedW - curW) > 1) {
        var f = curW / savedW;
        self.canvas.getObjects().forEach(function (obj) {
          if (obj._isGuide) return;
          obj.scaleX *= f;
          obj.scaleY *= f;
          obj.left *= f;
          obj.top *= f;
          obj.setCoords();
        });
        self.canvas.renderAll();
      }
      // Restored state becomes the new history baseline
      self.history = [];
      self.historyIndex = -1;
      self.saveState();
    });
  };

  // Clear autosave after successful add to cart
  InkbondStudio.prototype._clearAutoSave = function () {
    localStorage.removeItem(autosaveKey(this.mode.key));
    if (this.mode.key === 'single') localStorage.removeItem(AUTOSAVE_LEGACY_KEY);
  };

  // ── 3. Double-tap to edit text ──
  InkbondStudio.prototype._setupDoubleTapEdit = function () {
    var self = this;

    function isText(obj) {
      return obj && !obj._isGuide && (obj.type === 'i-text' || obj.type === 'textbox' || obj.type === 'text');
    }

    // Desktop: native double-click
    this.canvas.on('mouse:dblclick', function (e) {
      if (isText(e.target)) self.adoptTextObject(e.target);
    });

    // Touch: Fabric never fires mouse:dblclick from taps (touchstart is
    // preventDefault-ed, killing synthesized dblclick), so detect two quick
    // taps on the same text object ourselves.
    if (IS_TOUCH) {
      var lastTapTime = 0;
      var lastTapTarget = null;
      this.canvas.on('mouse:down', function (e) {
        var obj = e.target;
        var now = Date.now();
        if (isText(obj)) {
          if (obj === lastTapTarget && (now - lastTapTime) < 350) {
            lastTapTime = 0;
            lastTapTarget = null;
            self.adoptTextObject(obj);
            return;
          }
          lastTapTime = now;
          lastTapTarget = obj;
        } else {
          lastTapTime = 0;
          lastTapTarget = null;
        }
      });
    }
  };

  // ── Keyboard lift: keep the (collapsed) text sheet above the on-screen
  // keyboard so the canvas and the live-updating text stay visible while a
  // customer types. position:fixed elements otherwise end up BEHIND the iOS
  // keyboard (the layout viewport doesn't shrink, only the visual one does).
  InkbondStudio.prototype._setupKeyboardLift = function () {
    if (!window.visualViewport) return;
    var vv = window.visualViewport;
    function lift() {
      var sheet = document.querySelector('.ib-studio__bottom-sheet');
      if (!sheet) return;
      var ae = document.activeElement;
      var typing = ae && ae.classList && ae.classList.contains('js-studio-text-input');
      var kb = window.innerHeight - vv.height - vv.offsetTop;
      sheet.style.transform = (typing && kb > 60) ? 'translateY(-' + Math.round(kb) + 'px)' : '';
    }
    vv.addEventListener('resize', lift);
    vv.addEventListener('scroll', lift);
    document.addEventListener('focusout', function () { setTimeout(lift, 80); });
  };

  // ── 4. Leave page warning ──
  InkbondStudio.prototype._setupLeaveWarning = function () {
    var self = this;
    this._addingToCart = false;
    window.addEventListener('beforeunload', function (e) {
      if (self._addingToCart) return; // Skip warning when adding to cart
      var objs = self.canvas.getObjects().filter(function (o) { return !o._isGuide && !o._isSnapLine; });
      if (objs.length > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    });
  };

  // Sync mobile toolbar height to match canvas
  InkbondStudio.prototype._syncToolbarHeight = function () {
    var wrapper = document.getElementById('ib-studio-canvas-wrapper');
    if (wrapper) {
      var h = wrapper.offsetHeight;
      document.documentElement.style.setProperty('--ib-canvas-height', h + 'px');
    }
  };

  // Effective print DPI of an image at its current size on the canvas.
  // printed inches = (screen width / canvas width) * mode physical width.
  InkbondStudio.prototype._imageEffectiveDPI = function (obj) {
    if (!obj || obj.type !== 'image') return null;
    var el = obj.getElement && obj.getElement();
    var naturalW = el ? (el.naturalWidth || el.width) : obj.width;
    if (!naturalW) return null;
    var printedInches = (obj.getScaledWidth() / this.canvas.getWidth()) * this.mode.widthInches;
    if (!printedInches || printedInches <= 0) return null;
    return Math.round(naturalW / printedInches);
  };

  // Helper: lock image to corner-only scaling
  InkbondStudio.prototype._lockImageControls = function (img) {
    img.setControlsVisibility({
      mt: false, mb: false, ml: false, mr: false, // hide side handles
      tl: true, tr: true, bl: true, br: true,     // keep corners
      mtr: true                                     // keep rotation
    });
  };

  // ── Image upload (auto-placed on canvas) ──
  InkbondStudio.prototype.addImage = function (file) {
    var self = this;
    if (file.size > 10 * 1024 * 1024) {
      this.toast('That file is too big — max 10MB.', 'error');
      return;
    }
    var reader = new FileReader();
    reader.onload = function (e) {
      var imgEl = new Image();
      imgEl.onload = function () {
        var fabricImg = new fabric.Image(imgEl);
        var maxW = self.canvas.getWidth() * 0.55;
        var maxH = self.canvas.getHeight() * 0.55;
        var scale = Math.min(maxW / fabricImg.width, maxH / fabricImg.height, 1);

        fabricImg.set({
          scaleX: scale, scaleY: scale,
          left: (self.canvas.getWidth() - fabricImg.width * scale) / 2,
          top: (self.canvas.getHeight() - fabricImg.height * scale) / 2,
          lockUniScaling: true
        });
        self._lockImageControls(fabricImg);

        self.canvas.add(fabricImg);
        self.canvas.setActiveObject(fabricImg);
        self.canvas.renderAll();
        self.saveState();
        self.updateHasObjects();
        // The upload is auto-selected, so the context bar with Remove BG /
        // Face Cutout is now attached to the photo — pulse it so it can't
        // be missed on first upload.
        self._pulsePhotoTools();
        self._showUploadHint();
      };
      imgEl.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Show one-time tip after photo upload (points at the Remove BG tools)
  InkbondStudio.prototype._showUploadHint = function () {
    if (this._uploadHintShown) return;
    this._uploadHintShown = true;
    this.toast('✨ Try Remove BG or Face Cutout — right above your photo');
  };

  // Legacy addText kept for backward compat but now uses openTextPanel flow
  InkbondStudio.prototype.addText = function (customText) {
    var text = new fabric.IText(customText || 'Your text here', {
      left: this.canvas.getWidth() / 2,
      top: this.canvas.getHeight() / 2,
      originX: 'center',
      originY: 'center',
      fontSize: this.selectedSize,
      fontFamily: this.selectedFont,
      fill: this.selectedColor,
      fontWeight: 'normal',
      fontStyle: 'normal',
      paintFirst: 'stroke',
      lockUniScaling: true
    });
    this._lockImageControls(text);
    this.canvas.add(text);
    this.canvas.setActiveObject(text);
    this.canvas.renderAll();
    this.saveState();
    this.updateHasObjects();
  };

  InkbondStudio.prototype.duplicateSelected = function () {
    var self = this;
    var active = this.canvas.getActiveObject();
    if (!active || active._isGuide) return;

    function handleClone(cloned) {
      cloned.set({ left: active.left + 20, top: active.top + 20 });
      if (cloned.type === 'image' && cloned.lockUniScaling) self._lockImageControls(cloned);
      self.canvas.add(cloned);
      self.canvas.setActiveObject(cloned);
      self.canvas.renderAll();
      self.saveState();
      self.updateHasObjects();
      self.refreshLayers();
    }

    var result = active.clone();
    // Fabric.js v6 returns a Promise
    if (result && typeof result.then === 'function') {
      result.then(handleClone).catch(function (e) { console.warn('Clone failed:', e); });
    } else if (result) {
      handleClone(result);
    }
  };

  InkbondStudio.prototype.deleteSelected = function () {
    var active = this.canvas.getActiveObject();
    if (!active || active._isGuide) return;

    // If deleting the activeTextObj, clear reference
    if (active === this.activeTextObj) {
      this.activeTextObj = null;
      this._setAlpineData('textEditing', false);
    }

    if (active.type === 'activeSelection') {
      var self = this;
      var toRemove = [];
      active.forEachObject(function (obj) {
        if (!obj._isGuide) toRemove.push(obj);
      });
      this.canvas.discardActiveObject();
      toRemove.forEach(function (obj) { self.canvas.remove(obj); });
    } else {
      this.canvas.remove(active);
    }
    this.canvas.renderAll();
    this.saveState();
    this.updateHasObjects();
    this.refreshLayers();
  };

  // ── Layer ordering ──
  InkbondStudio.prototype.bringToFront = function () {
    var active = this.canvas.getActiveObject();
    if (active && !active._isGuide) { this.canvas.bringObjectToFront(active); this.canvas.renderAll(); this.saveState(); }
  };
  InkbondStudio.prototype.sendToBack = function () {
    var active = this.canvas.getActiveObject();
    if (active && !active._isGuide) { this.canvas.sendObjectToBack(active); this.canvas.renderAll(); this.saveState(); }
  };
  InkbondStudio.prototype.bringForward = function () {
    var active = this.canvas.getActiveObject();
    if (active && !active._isGuide) { this.canvas.bringObjectForward(active); this.canvas.renderAll(); this.saveState(); }
  };
  InkbondStudio.prototype.sendBackward = function () {
    var active = this.canvas.getActiveObject();
    if (active && !active._isGuide) { this.canvas.sendObjectBackwards(active); this.canvas.renderAll(); this.saveState(); }
  };

  InkbondStudio.prototype.clearCanvas = function () {
    if (!confirm('Clear your entire design? This cannot be undone.')) return;
    var self = this;
    var objects = this.canvas.getObjects().slice();
    objects.forEach(function (obj) { if (!obj._isGuide) self.canvas.remove(obj); });
    this.canvas.discardActiveObject();
    this.activeTextObj = null;
    this.canvas.renderAll();
    this.saveState();
    this.updateHasObjects();
    this.refreshLayers();
  };

  InkbondStudio.prototype.updateHasObjects = function () {
    var count = this.canvas.getObjects().filter(function (obj) { return !obj._isGuide; }).length;
    var el = document.getElementById('inkbond-studio');
    if (el && el._x_dataStack) {
      el._x_dataStack.forEach(function (data) {
        if (data.hasObjects !== undefined) data.hasObjects = count > 0;
      });
    }
  };

  // ── Text Property Updates ──
  // These update the active text object OR the selected canvas object
  InkbondStudio.prototype._getTextTarget = function () {
    // Prefer the active text object from the panel, then fall back to canvas selection
    if (this.activeTextObj && this.canvas.getObjects().indexOf(this.activeTextObj) !== -1) {
      return this.activeTextObj;
    }
    var obj = this.canvas.getActiveObject();
    if (obj && (obj.type === 'i-text' || obj.type === 'textbox')) return obj;
    return null;
  };

  // `commit` (default true) controls whether the change is pushed to undo
  // history. Continuous controls (sliders, color-picker drags) pass false on
  // 'input' and commit once on 'change' — one drag used to flood all 50
  // history slots and evict the user's earlier design states.
  InkbondStudio.prototype.updateTextColor = function (color, commit) {
    if (!color) { this.updatePreview(); return; } // guard: null fill = invisible text
    this.selectedColor = color;
    var obj = this._getTextTarget();
    if (obj) {
      obj.set('fill', color);
      this.canvas.renderAll();
      if (commit !== false) this.saveState();
    }
    this.updatePreview();
  };

  InkbondStudio.prototype.updateTextFont = function (font) {
    var self = this;
    this.selectedFont = font;
    this.updatePreview();
    var obj = this._getTextTarget();
    if (obj) {
      // Load the web font before applying, or the canvas paints the fallback
      // and Fabric caches wrong glyph metrics for this family.
      this._withFont(font, function () {
        obj.set('fontFamily', font);
        self.canvas.requestRenderAll();
        self.saveState();
      });
    }
  };

  InkbondStudio.prototype.updateTextSize = function (size, commit) {
    this.selectedSize = size;
    var obj = this._getTextTarget();
    if (obj) {
      obj.set('fontSize', size);
      this.canvas.renderAll();
      if (commit !== false) this.saveState();
    }
    this.updatePreview();
  };

  InkbondStudio.prototype.toggleBold = function () {
    var obj = this._getTextTarget();
    if (!obj) {
      // Compose mode: remember for the next Add and show it in the preview
      this.selectedWeight = this.selectedWeight === 'bold' ? 'normal' : 'bold';
      this.updatePreview();
      return this.selectedWeight === 'bold';
    }
    var isBold = obj.fontWeight === 'bold';
    obj.set('fontWeight', isBold ? 'normal' : 'bold');
    this.selectedWeight = isBold ? 'normal' : 'bold';
    this.canvas.renderAll();
    this.saveState();
    this.updatePreview();
    return !isBold;
  };

  InkbondStudio.prototype.toggleItalic = function () {
    var obj = this._getTextTarget();
    if (!obj) {
      this.selectedStyle = this.selectedStyle === 'italic' ? 'normal' : 'italic';
      this.updatePreview();
      return this.selectedStyle === 'italic';
    }
    var isItalic = obj.fontStyle === 'italic';
    obj.set('fontStyle', isItalic ? 'normal' : 'italic');
    this.selectedStyle = isItalic ? 'normal' : 'italic';
    this.canvas.renderAll();
    this.saveState();
    this.updatePreview();
    return !isItalic;
  };

  // ── Text Outline (stroke) ──
  InkbondStudio.prototype.updateTextStroke = function (color, commit) {
    this.selectedStroke = color || null;

    // Keep the pending stroke width sane in BOTH compose and edit mode:
    // picking a color implies at least 1px; OFF resets the width.
    var sliders = document.querySelectorAll('.js-studio-outline-slider');
    var values = document.querySelectorAll('.js-studio-outline-value');
    if (color && (!this.selectedStrokeWidth || this.selectedStrokeWidth === 0)) {
      this.selectedStrokeWidth = 1;
      sliders.forEach(function (sl) { sl.value = 1; });
      values.forEach(function (sv) { sv.textContent = '1px'; });
    }
    if (!color) {
      this.selectedStrokeWidth = 0;
      sliders.forEach(function (sl) { sl.value = 0; });
      values.forEach(function (sv) { sv.textContent = '0px'; });
    }

    var obj = this._getTextTarget();
    if (obj) {
      if (!color) {
        obj.set({ stroke: null, strokeWidth: 0 });
      } else {
        obj.set({ stroke: color, paintFirst: 'stroke' });
        if (!obj.strokeWidth || obj.strokeWidth === 0) {
          obj.set('strokeWidth', this.selectedStrokeWidth);
        }
      }
      this.canvas.renderAll();
      if (commit !== false) this.saveState();
    }
    this.updatePreview();
  };

  InkbondStudio.prototype.updateTextStrokeWidth = function (width, commit) {
    this.selectedStrokeWidth = width;
    var obj = this._getTextTarget();
    if (!obj) { this.updatePreview(); return; }
    obj.set('strokeWidth', width);
    this.canvas.renderAll();
    if (commit !== false) this.saveState();
    this.updatePreview();
  };

  // ── Image Processing (cutout.pro via Worker) ──
  InkbondStudio.prototype.processImage = function (type) {
    var self = this;
    var obj = this.canvas.getActiveObject();
    if (!obj || obj.type !== 'image') return Promise.reject(new Error('Tap your photo on the canvas first, then try again.'));
    if (!WORKER_URL) return Promise.reject(new Error('Worker URL not configured'));

    var endpoint = type === 'face-cutout' ? '/face-cutout' : '/bg-remove';

    // Show processing state on Alpine
    this._setAlpineData('isProcessing', true);

    // Get the image element's source and downscale if needed (per-mode cap —
    // full sheets keep more pixels so 8.5x11 prints stay sharp)
    var imgEl = obj.getElement();
    var origW = imgEl.naturalWidth || imgEl.width;
    var origH = imgEl.naturalHeight || imgEl.height;
    var maxDim = this.mode.maxUploadDim || 2048;
    var scale = Math.min(maxDim / origW, maxDim / origH, 1);
    var tempCanvas = document.createElement('canvas');
    tempCanvas.width = Math.round(origW * scale);
    tempCanvas.height = Math.round(origH * scale);
    var ctx = tempCanvas.getContext('2d');
    ctx.drawImage(imgEl, 0, 0, tempCanvas.width, tempCanvas.height);

    return new Promise(function (resolve, reject) {
      tempCanvas.toBlob(function (blob) {
        if (!blob) { self._setAlpineData('isProcessing', false); reject(new Error('Failed to read image')); return; }

        var formData = new FormData();
        formData.append('file', blob, 'image.png');

        // 25s timeout to avoid hanging forever
        var controller = new AbortController();
        var timeoutId = setTimeout(function () { controller.abort(); }, 25000);

        fetch(WORKER_URL + endpoint, { method: 'POST', body: formData, signal: controller.signal })
          .then(function (res) { clearTimeout(timeoutId); return res.json(); })
          .then(function (data) {
            if (!data.success || !data.image) {
              throw new Error(data.error || 'Processing failed');
            }
            var dataUrl = 'data:image/png;base64,' + data.image;
            return self._replaceActiveImage(obj, dataUrl);
          })
          .then(function () {
            self._setAlpineData('isProcessing', false);
            resolve();
          })
          .catch(function (err) {
            clearTimeout(timeoutId);
            self._setAlpineData('isProcessing', false);
            if (err.name === 'AbortError') {
              reject(new Error('Processing timed out. Try a smaller image.'));
            } else {
              reject(err);
            }
          });
      }, 'image/jpeg', 0.85); // Send as JPEG to reduce upload size
    });
  };

  InkbondStudio.prototype._replaceActiveImage = function (oldObj, dataUrl) {
    var self = this;
    return new Promise(function (resolve) {
      var imgEl = new Image();
      imgEl.onload = function () {
        var newImg = new fabric.Image(imgEl);
        // Preserve position, scale, rotation from original
        newImg.set({
          left: oldObj.left,
          top: oldObj.top,
          scaleX: oldObj.scaleX * (oldObj.width / newImg.width),
          scaleY: oldObj.scaleY * (oldObj.height / newImg.height),
          angle: oldObj.angle || 0,
          lockUniScaling: true,
          originX: oldObj.originX,
          originY: oldObj.originY
        });
        self._lockImageControls(newImg);
        self.canvas.remove(oldObj);
        self.canvas.add(newImg);
        self.canvas.setActiveObject(newImg);
        self.canvas.renderAll();
        self.saveState();
        resolve();
      };
      imgEl.src = dataUrl;
    });
  };

  // ── Art Library ──
  InkbondStudio.prototype.addArtToCanvas = function (imageUrl) {
    var self = this;
    var imgEl = new Image();
    imgEl.crossOrigin = 'anonymous';
    imgEl.onload = function () {
      var fabricImg = new fabric.Image(imgEl);
      var maxW = self.canvas.getWidth() * 0.55;
      var maxH = self.canvas.getHeight() * 0.55;
      var scale = Math.min(maxW / fabricImg.width, maxH / fabricImg.height, 1);

      fabricImg.set({
        scaleX: scale, scaleY: scale,
        left: (self.canvas.getWidth() - fabricImg.width * scale) / 2,
        top: (self.canvas.getHeight() - fabricImg.height * scale) / 2,
        lockUniScaling: true
      });
      self._lockImageControls(fabricImg);

      self.canvas.add(fabricImg);
      self.canvas.setActiveObject(fabricImg);
      self.canvas.renderAll();
      self.saveState();
      self.updateHasObjects();
    };
    imgEl.src = imageUrl;
  };

  // ── Alpine.js data helper ──
  InkbondStudio.prototype._setAlpineData = function (key, value) {
    var el = document.getElementById('inkbond-studio');
    if (el && el._x_dataStack) {
      el._x_dataStack.forEach(function (data) {
        if (data[key] !== undefined) data[key] = value;
      });
    }
  };

  // ── Selection context bar (any object: text OR image) ──
  // The single "edit this layer" surface: appears attached to whatever is
  // selected, with type-specific actions. This is how customers discover
  // Remove BG / Face Cutout — the buttons show up ON the photo they just
  // uploaded (uploads are auto-selected).
  InkbondStudio.prototype._showCtxBar = function (obj) {
    var bar = document.getElementById('ib-studio-ctx-bar');
    if (!bar || !obj || obj._isGuide || obj.type === 'activeSelection') {
      this._hideCtxBar();
      return;
    }
    // Respect the ✕ dismissal while the same object stays selected — without
    // this, object:modified re-shows the bar after every nudge
    if (this._ctxDismissed === obj) { this._hideCtxBar(); return; }
    if (this._ctxDismissed && this._ctxDismissed !== obj) this._ctxDismissed = null;
    var isImage = obj.type === 'image';
    var isText = obj.type === 'i-text' || obj.type === 'textbox';
    bar.querySelectorAll('.js-ctx-img-only').forEach(function (b) { b.style.display = isImage ? 'flex' : 'none'; });
    bar.querySelectorAll('.js-ctx-text-only').forEach(function (b) { b.style.display = isText ? 'flex' : 'none'; });

    bar.style.display = 'flex';
    // The bar lives in .ib-studio__canvas-area (NOT inside the canvas
    // wrapper, whose overflow:hidden would clip it) — offset by the
    // wrapper's position and clamp within the area.
    var wrapper = document.getElementById('ib-studio-canvas-wrapper');
    var area = document.querySelector('.ib-studio__canvas-area');
    var offX = 0, offY = 0, areaW = this.canvas.getWidth();
    if (wrapper && area) {
      var wr = wrapper.getBoundingClientRect();
      var ar = area.getBoundingClientRect();
      offX = wr.left - ar.left;
      offY = wr.top - ar.top;
      areaW = ar.width;
    }
    var barW = bar.offsetWidth || 200;
    var barH = bar.offsetHeight || 34;
    var bound = obj.getBoundingRect();
    // Bar uses translate(-50%, -100%): clamp center within the area, and
    // keep it below the area's top edge.
    var cx = offX + bound.left + bound.width / 2;
    cx = Math.max(barW / 2 + 4, Math.min(cx, areaW - barW / 2 - 4));
    bar.style.left = cx + 'px';
    bar.style.top = Math.max(offY + bound.top - 8, barH + 8) + 'px';
  };

  InkbondStudio.prototype._hideCtxBar = function () {
    var bar = document.getElementById('ib-studio-ctx-bar');
    if (bar) bar.style.display = 'none';
  };

  // ✕ on the context bar: keep it hidden while this object stays selected;
  // it returns when the customer selects something else (or re-selects)
  InkbondStudio.prototype.dismissCtxBar = function () {
    this._ctxDismissed = this.canvas.getActiveObject() || null;
    this._hideCtxBar();
  };

  // Context-bar "Edit" action: route to the right editing surface
  InkbondStudio.prototype.editSelected = function () {
    var obj = this.canvas.getActiveObject();
    if (!obj || obj._isGuide) return;
    if (obj.type === 'i-text' || obj.type === 'textbox') {
      this.adoptTextObject(obj);
    } else if (obj.type === 'image') {
      this._setAlpineData('activePanel', 'upload');
    }
  };

  // One-time attention pulse on the photo magic buttons after first upload
  InkbondStudio.prototype._pulsePhotoTools = function () {
    if (this._photoToolsPulsed) return;
    this._photoToolsPulsed = true;
    var bar = document.getElementById('ib-studio-ctx-bar');
    if (!bar) return;
    bar.querySelectorAll('.ib-studio__ctx-btn--magic').forEach(function (b) {
      b.classList.add('pulse');
      setTimeout(function () { b.classList.remove('pulse'); }, 3600);
    });
  };

  // (Edit pencil removed — the selection context bar replaced it.)

  // ── Export ──
  InkbondStudio.prototype.prepareExport = function () {
    var self = this;
    return new Promise(function (resolve) {
      // Print-quality guard: photos stretched past ~150 effective DPI.
      // (The old outside-the-printable-area warning is gone — the preview
      // image itself shows exactly what prints, cropped edges included.)
      var lowRes = self.canvas.getObjects().filter(function (o) {
        if (o._isGuide || o.type !== 'image') return false;
        var dpi = self._imageEffectiveDPI(o);
        return dpi !== null && dpi < 150;
      });
      var warnings = [];
      if (lowRes.length) warnings.push('A photo in your design is stretched beyond its resolution and may print blurry — shrink it a little or upload a higher-quality photo.');
      self._setAlpineData('exportWarning', warnings.join(' '));

      self.canvas.getObjects().forEach(function (obj) {
        if (obj._isGuide) obj.set('visible', false);
      });
      self.canvas.discardActiveObject();

      // Temporarily set white background for preview
      var origBg = self.canvas.backgroundColor;
      self.canvas.backgroundColor = '#ffffff';
      self.canvas.renderAll();

      var preview = self.canvas.toDataURL({ format: 'jpeg', quality: 0.85, multiplier: 1 });

      // Restore transparent background
      self.canvas.backgroundColor = origBg;
      self.canvas.getObjects().forEach(function (obj) {
        if (obj._isGuide) obj.set('visible', true);
      });
      self.canvas.renderAll();

      self.previewDataUrl = preview;
      resolve(preview);
    });
  };

  // Helper: convert data URL to Blob for upload
  InkbondStudio.prototype._dataUrlToBlob = function (dataUrl) {
    var parts = dataUrl.split(',');
    var mime = parts[0].match(/:(.*?);/)[1];
    var byteString = atob(parts[1]);
    var ab = new ArrayBuffer(byteString.length);
    var ia = new Uint8Array(ab);
    for (var i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
    return new Blob([ab], { type: mime });
  };

  // Upload image to Cloudflare R2 via Worker
  InkbondStudio.prototype._uploadImage = function (dataUrl, type) {
    var blob = this._dataUrlToBlob(dataUrl);
    var form = new FormData();
    form.append('file', blob, type === 'preview' ? 'preview.jpg' : 'production.png');
    form.append('type', type);

    return fetch(WORKER_URL + '/upload', { method: 'POST', body: form })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (!data.success) throw new Error(data.error || 'Upload failed');
        return data.url;
      });
  };

  InkbondStudio.prototype.addToCart = function (variantId) {
    var self = this;
    var vid = variantId || this.mode.variants[0].id;
    return new Promise(function (resolve, reject) {
      if (!self.previewDataUrl) { reject(new Error('No preview generated')); return; }
      if (!vid) { reject(new Error('No variant selected')); return; }

      // Generate high-res transparent PNG production export (300 DPI)
      self.canvas.getObjects().forEach(function (obj) {
        if (obj._isGuide) obj.set('visible', false);
      });
      self.canvas.discardActiveObject();

      // Temporarily remove background for transparent export
      var origBg = self.canvas.backgroundColor;
      self.canvas.backgroundColor = null;
      self.canvas.renderAll();

      var productionDataUrl = self.canvas.toDataURL({
        format: 'png', quality: 1,
        multiplier: self.mode.prodW / self.canvas.getWidth()
      });

      // Restore background and guides
      self.canvas.backgroundColor = origBg;
      self.canvas.getObjects().forEach(function (obj) {
        if (obj._isGuide) obj.set('visible', true);
      });
      self.canvas.renderAll();

      // Upload both preview and production images to R2
      Promise.all([
        self._uploadImage(self.previewDataUrl, 'preview'),
        self._uploadImage(productionDataUrl, 'production')
      ])
      .then(function (urls) {
        var previewUrl = urls[0];
        var prodUrl = urls[1];

        var props = {
          '_customily-preview': previewUrl,
          '_customily-production-url': prodUrl,
          '_customily-shop-preview': previewUrl,
          '_customily-thumb': previewUrl,
          '_studio-design': 'true',
          '_studio-size': self.mode.sizeProp
        };

        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: vid, quantity: 1, properties: props }] })
        });
      })
      .then(function (res) { if (!res.ok) throw new Error('Cart add failed'); return res.json(); })
      .then(function () { self._clearAutoSave(); resolve(); })
      .catch(function (err) { reject(err); });
    });
  };

  // ── Event Bindings ──
  InkbondStudio.prototype.bindEvents = function () {
    var self = this;

    // File input
    var fileInput = document.getElementById('ib-studio-file-input');
    if (fileInput) {
      fileInput.addEventListener('change', function (e) {
        var files = e.target.files;
        for (var i = 0; i < files.length; i++) {
          if (files[i].type.indexOf('image') === 0) self.addImage(files[i]);
        }
        fileInput.value = '';
      });
    }

    // Drag & drop on upload zone
    var dropZone = document.getElementById('ib-studio-drop-zone');
    if (dropZone) {
      dropZone.addEventListener('dragover', function (e) {
        e.preventDefault();
        dropZone.style.borderColor = '#c4a882';
        dropZone.style.background = '#faf6f0';
      });
      dropZone.addEventListener('dragleave', function () {
        dropZone.style.borderColor = '#d0d0d0';
        dropZone.style.background = '';
      });
      dropZone.addEventListener('drop', function (e) {
        e.preventDefault();
        dropZone.style.borderColor = '#d0d0d0';
        dropZone.style.background = '';
        var files = e.dataTransfer.files;
        for (var i = 0; i < files.length; i++) {
          if (files[i].type.indexOf('image') === 0) self.addImage(files[i]);
        }
      });
    }

    // Also allow drag & drop on canvas
    var canvasArea = document.querySelector('.ib-studio__canvas-area');
    if (canvasArea) {
      canvasArea.addEventListener('dragover', function (e) { e.preventDefault(); });
      canvasArea.addEventListener('drop', function (e) {
        e.preventDefault();
        var files = e.dataTransfer.files;
        for (var i = 0; i < files.length; i++) {
          if (files[i].type.indexOf('image') === 0) self.addImage(files[i]);
        }
      });
    }

    // ── Live text input: update active text object as user types ──
    var textInputs = document.querySelectorAll('.js-studio-text-input');
    textInputs.forEach(function (input) {
      input.addEventListener('input', function () {
        // Sync all text inputs
        var val = this.value;
        textInputs.forEach(function (inp) {
          if (inp !== input) inp.value = val;
        });
        self.updateLiveText(val);
      });
    });

    // Object modified -> save state, re-anchor the context bar
    this.canvas.on('object:modified', function (e) {
      self.saveState();
      var active = self.canvas.getActiveObject();
      if (active && !active._isGuide) self._showCtxBar(active);
      // Print-quality guard: warn once per image when it's stretched past
      // what its resolution can print sharply at this canvas size
      var obj = e && e.target;
      if (obj && obj.type === 'image' && !obj._dpiWarned) {
        var dpi = self._imageEffectiveDPI(obj);
        if (dpi !== null && dpi < 150) {
          obj._dpiWarned = true;
          self.toast('Heads up: at this size your photo may print a little blurry. Smaller = sharper.', 'error');
        }
      }
    });

    // Selection events -> sync panels + context bar + layers
    this.canvas.on('selection:created', function (e) {
      self._setAlpineData('hasSelection', true);
      self.syncPanelToSelection(e);
      self.refreshLayers();
      var obj = e.selected ? e.selected[0] : null;
      if (obj && obj.type === 'image') self._showPinchHint();
      if (obj && !obj._isGuide) self._showCtxBar(obj);
      else self._hideCtxBar();
    });
    this.canvas.on('selection:updated', function (e) {
      self._setAlpineData('hasSelection', true);
      self.syncPanelToSelection(e);
      self.refreshLayers();
      var obj = e.selected ? e.selected[0] : null;
      if (obj && !obj._isGuide) self._showCtxBar(obj);
      else self._hideCtxBar();
    });
    this.canvas.on('selection:cleared', function () {
      self._setAlpineData('hasSelection', false);
      self._ctxDismissed = null;
      self._hideCtxBar();
      self._setAlpineData('textEditing', false);
      self.activeTextObj = null;
      self.refreshLayers();
    });
    this.canvas.on('object:moving', function () { self._hideCtxBar(); });
    this.canvas.on('object:scaling', function (e) {
      self._hideCtxBar();
      var obj = e.target;
      if (!obj) return;
      // Cap image scaling relative to raw pixels — per size mode (a full
      // sheet legitimately needs larger blowups than a 2x3.5 single)
      if (obj.type === 'image') {
        var maxScale = self.mode.maxImgScale || 4.0;
        if (obj.scaleX > maxScale) { obj.scaleX = maxScale; obj.scaleY = maxScale; }
        if (obj.scaleY > maxScale) { obj.scaleY = maxScale; obj.scaleX = maxScale; }
      }
    });

    // ── Color swatches (both desktop + mobile via querySelectorAll) ──
    // Exclude outline swatches AND the custom-picker label — the label has no
    // data-color, so letting it through set fill to null (invisible text).
    var allSwatches = document.querySelectorAll('.ib-studio__swatch:not(.js-studio-outline-swatch):not(.ib-studio__swatch--picker)');
    allSwatches.forEach(function (swatch) {
      swatch.addEventListener('click', function () {
        allSwatches.forEach(function (s) { s.classList.remove('active'); });
        allSwatches.forEach(function (s) {
          if (s.getAttribute('data-color') === swatch.getAttribute('data-color')) s.classList.add('active');
        });
        self.updateTextColor(swatch.getAttribute('data-color'));
      });
    });

    // ── Font picker (both desktop + mobile) ──
    var allFontBtns = document.querySelectorAll('.ib-studio__font-card');
    allFontBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        allFontBtns.forEach(function (b) { b.classList.remove('active'); });
        allFontBtns.forEach(function (b) {
          if (b.getAttribute('data-font') === btn.getAttribute('data-font')) b.classList.add('active');
        });
        self.updateTextFont(btn.getAttribute('data-font'));
      });
    });

    // ── Size sliders (all of them) ──
    // Live-update on 'input', commit ONE undo state on 'change' — a single
    // drag used to push ~50 history entries and evict earlier design states.
    var sizeSliders = document.querySelectorAll('.js-studio-size-slider');
    var sizeValues = document.querySelectorAll('.js-studio-size-value');
    sizeSliders.forEach(function (slider) {
      slider.addEventListener('input', function () {
        var val = parseInt(this.value, 10);
        // Sync all size displays
        sizeValues.forEach(function (sv) { sv.textContent = val + 'px'; });
        sizeSliders.forEach(function (sl) { sl.value = val; });
        self.updateTextSize(val, false);
      });
      slider.addEventListener('change', function () {
        if (self._getTextTarget()) self.saveState();
      });
    });

    // ── Bold / Italic buttons (all of them) ──
    var boldBtns = document.querySelectorAll('.js-studio-bold');
    boldBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var isBold = self.toggleBold();
        boldBtns.forEach(function (b) { b.classList.toggle('active', isBold); });
      });
    });

    var italicBtns = document.querySelectorAll('.js-studio-italic');
    italicBtns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var isItalic = self.toggleItalic();
        italicBtns.forEach(function (b) { b.classList.toggle('active', isItalic); });
      });
    });

    // ── Outline color swatches (both desktop + mobile) ──
    var outlineSwatches = document.querySelectorAll('.js-studio-outline-swatch');
    outlineSwatches.forEach(function (swatch) {
      swatch.addEventListener('click', function () {
        outlineSwatches.forEach(function (s) { s.classList.remove('active'); });
        var color = swatch.getAttribute('data-outline-color');
        outlineSwatches.forEach(function (s) {
          if (s.getAttribute('data-outline-color') === color) s.classList.add('active');
        });
        self.updateTextStroke(color || null);
      });
    });

    // ── Outline color picker ──
    var outlineColorPickers = document.querySelectorAll('.js-studio-outline-color-picker');
    outlineColorPickers.forEach(function (picker) {
      picker.addEventListener('input', function (e) {
        e.stopPropagation();
        var color = this.value;
        outlineSwatches.forEach(function (s) { s.classList.remove('active'); });
        this.closest('.ib-studio__swatch--picker').style.background = color;
        self.updateTextStroke(color, false);
        var previews = document.querySelectorAll('.js-studio-text-preview-text');
        previews.forEach(function (el) {
          el.style.webkitTextStrokeColor = color;
        });
      });
      picker.addEventListener('change', function (e) {
        e.stopPropagation();
        self.updateTextStroke(this.value, true);
      });
    });

    // ── Outline thickness sliders ──
    var outlineSliders = document.querySelectorAll('.js-studio-outline-slider');
    var outlineValues = document.querySelectorAll('.js-studio-outline-value');
    outlineSliders.forEach(function (slider) {
      slider.addEventListener('input', function () {
        var val = parseInt(this.value, 10);
        outlineValues.forEach(function (sv) { sv.textContent = val + 'px'; });
        outlineSliders.forEach(function (sl) { sl.value = val; });
        self.updateTextStrokeWidth(val, false);
      });
      slider.addEventListener('change', function () {
        if (self._getTextTarget()) self.saveState();
      });
    });

    // ── Image scale sliders ──
    var imgScaleSliders = document.querySelectorAll('.js-studio-img-scale');
    var imgScaleValues = document.querySelectorAll('.js-studio-img-scale-value');
    imgScaleSliders.forEach(function (slider) {
      slider.addEventListener('input', function () {
        var pct = parseInt(this.value, 10);
        imgScaleValues.forEach(function (sv) { sv.textContent = pct + '%'; });
        imgScaleSliders.forEach(function (sl) { sl.value = pct; });
        var obj = self.canvas.getActiveObject();
        if (obj && obj.type === 'image') {
          var baseScale = obj._baseScale || obj.scaleX;
          if (!obj._baseScale) obj._baseScale = obj.scaleX;
          var newScale = baseScale * (pct / 100);
          obj.set({ scaleX: newScale, scaleY: newScale });
          obj.setCoords();
          self.canvas.renderAll();
        }
      });
      slider.addEventListener('change', function () { self.saveState(); });
    });

    // ── Custom color picker (both desktop + mobile) ──
    var colorPickers = document.querySelectorAll('.js-studio-color-picker');
    colorPickers.forEach(function (picker) {
      picker.addEventListener('input', function (e) {
        e.stopPropagation();
        var color = this.value;
        allSwatches.forEach(function (s) { s.classList.remove('active'); });
        this.closest('.ib-studio__swatch--picker').style.background = color;
        // live preview only — no undo entry per drag tick
        self.updateTextColor(color, false);
      });
      picker.addEventListener('change', function (e) {
        e.stopPropagation();
        // 'change' fires when picker is closed — final commit
        self.updateTextColor(this.value, true);
      });
    });

    // ── Curve sliders ──
    var curveSliders = document.querySelectorAll('.js-studio-curve-slider');
    var curveValues = document.querySelectorAll('.js-studio-curve-value');
    curveSliders.forEach(function (slider) {
      slider.addEventListener('input', function () {
        var val = parseInt(this.value, 10);
        curveValues.forEach(function (sv) { sv.textContent = val; });
        curveSliders.forEach(function (sl) { sl.value = val; });
        self.curveText(val, false);
      });
      slider.addEventListener('change', function () {
        if (self._getTextTarget()) self.saveState();
      });
    });

    // ── Inline canvas edits (desktop IText editing) sync back to the panel ──
    // Without this, a later panel keystroke reverts inline edits to the stale
    // panel value.
    this.canvas.on('text:changed', function (e) {
      var t = e.target;
      if (!t) return;
      document.querySelectorAll('.js-studio-text-input').forEach(function (inp) { inp.value = t.text || ''; });
      self.updatePreview(t.text || '');
    });
  };

  InkbondStudio.prototype.syncPanelToSelection = function (e) {
    var obj = e.selected ? e.selected[0] : null;
    if (!obj || (obj.type !== 'i-text' && obj.type !== 'textbox')) return;

    // Adopt the selected text object as the active one for panel controls
    this.activeTextObj = obj;
    this.selectedWeight = obj.fontWeight || 'normal';
    this.selectedStyle = obj.fontStyle || 'normal';
    this._setAlpineData('textEditing', true);

    // Hex compare must be case-insensitive: swatch data-colors are uppercase,
    // <input type=color> values are always lowercase.
    function norm(c) { return String(c || '').trim().toUpperCase(); }
    var fill = norm(obj.fill);

    // Sync fill swatches only (NOT outline swatches / picker labels — they
    // have no data-color, so the old catch-all wiped their active state)
    var swatches = document.querySelectorAll('.ib-studio__swatch:not(.js-studio-outline-swatch):not(.ib-studio__swatch--picker)');
    var matched = false;
    swatches.forEach(function (s) {
      var hit = norm(s.getAttribute('data-color')) === fill;
      if (hit) matched = true;
      s.classList.toggle('active', hit);
    });
    // Custom-picker colors: reflect them on the picker swatch itself
    document.querySelectorAll('.js-studio-color-picker').forEach(function (p) {
      var label = p.closest('.ib-studio__swatch--picker');
      if (!matched && /^#[0-9a-fA-F]{6}$/.test(String(obj.fill || ''))) {
        p.value = String(obj.fill).toLowerCase();
        if (label) label.style.background = obj.fill;
      } else if (label) {
        label.style.background = '';
      }
    });

    // Sync font cards
    var fontBtns = document.querySelectorAll('.ib-studio__font-card');
    fontBtns.forEach(function (b) {
      b.classList.toggle('active', b.getAttribute('data-font') === obj.fontFamily);
    });

    // Sync size sliders
    var sizeSliders = document.querySelectorAll('.js-studio-size-slider');
    var sizeValues = document.querySelectorAll('.js-studio-size-value');
    sizeSliders.forEach(function (sl) { sl.value = obj.fontSize; });
    sizeValues.forEach(function (sv) { sv.textContent = obj.fontSize + 'px'; });

    // Sync bold/italic
    var boldBtns = document.querySelectorAll('.js-studio-bold');
    var italicBtns = document.querySelectorAll('.js-studio-italic');
    boldBtns.forEach(function (b) { b.classList.toggle('active', obj.fontWeight === 'bold'); });
    italicBtns.forEach(function (b) { b.classList.toggle('active', obj.fontStyle === 'italic'); });

    // Sync text inputs
    var textInputs = document.querySelectorAll('.js-studio-text-input');
    textInputs.forEach(function (inp) { inp.value = obj.text || ''; });

    // Sync outline swatches (case-insensitive)
    var outlineSwatches = document.querySelectorAll('.js-studio-outline-swatch');
    var currentStroke = norm(obj.stroke);
    outlineSwatches.forEach(function (s) {
      s.classList.toggle('active', norm(s.getAttribute('data-outline-color')) === currentStroke);
    });

    // Sync outline sliders
    var outlineSliders = document.querySelectorAll('.js-studio-outline-slider');
    var outlineValues = document.querySelectorAll('.js-studio-outline-value');
    var sw = obj.strokeWidth || 0;
    outlineSliders.forEach(function (sl) { sl.value = sw; });
    outlineValues.forEach(function (sv) { sv.textContent = sw + 'px'; });

    // Sync curve slider — was never synced, so the slider lied about the
    // selected text and the first tick re-curved it from a stale baseline
    var curve = obj._curveAmount || 0;
    document.querySelectorAll('.js-studio-curve-slider').forEach(function (sl) { sl.value = curve; });
    document.querySelectorAll('.js-studio-curve-value').forEach(function (sv) { sv.textContent = curve; });
  };

  // ── Share Design ──
  InkbondStudio.prototype.shareDesign = function () {
    var self = this;
    // Generate preview image
    this.canvas.getObjects().forEach(function (obj) {
      if (obj._isGuide) obj.set('visible', false);
    });
    this.canvas.discardActiveObject();
    this.canvas.renderAll();

    var dataUrl = this.canvas.toDataURL({ format: 'png', quality: 1, multiplier: 2 });

    this.canvas.getObjects().forEach(function (obj) {
      if (obj._isGuide) obj.set('visible', true);
    });
    this.canvas.renderAll();

    // Try native share API (mobile), fall back to download
    if (navigator.share && navigator.canShare) {
      fetch(dataUrl)
        .then(function (res) { return res.blob(); })
        .then(function (blob) {
          var file = new File([blob], 'inkbond-design.png', { type: 'image/png' });
          if (navigator.canShare({ files: [file] })) {
            return navigator.share({
              title: 'My Inkbond Tattoo Design',
              text: 'Check out my custom tattoo design!',
              files: [file]
            });
          } else {
            self._downloadDesign(dataUrl);
          }
        })
        .catch(function () { self._downloadDesign(dataUrl); });
    } else {
      this._downloadDesign(dataUrl);
    }
  };

  InkbondStudio.prototype._downloadDesign = function (dataUrl) {
    var link = document.createElement('a');
    link.download = 'inkbond-design.png';
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── Pinch-to-Zoom (mobile) ──
  InkbondStudio.prototype._setupPinchZoom = function () {
    var self = this;
    var lastDist = 0;
    var initialScaleX = 1;
    var initialScaleY = 1;
    var isPinching = false;
    var pinchTarget = null;

    function getDistance(touches) {
      var dx = touches[0].clientX - touches[1].clientX;
      var dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    }

    var wrapper = document.getElementById('ib-studio-canvas-wrapper');
    if (!wrapper) return;

    function endPinch(commit) {
      if (!isPinching && !pinchTarget) return;
      isPinching = false;
      if (pinchTarget) {
        // Restore drag lock taken at pinch start
        pinchTarget.lockMovementX = false;
        pinchTarget.lockMovementY = false;
        pinchTarget.setCoords();
        if (commit) self.saveState();
      }
      pinchTarget = null;
    }

    wrapper.addEventListener('touchstart', function (e) {
      if (e.touches.length === 2) {
        var active = self.canvas.getActiveObject();
        if (active && !active._isGuide) {
          isPinching = true;
          pinchTarget = active;
          lastDist = getDistance(e.touches);
          initialScaleX = active.scaleX;
          initialScaleY = active.scaleY;
          // Abort Fabric's in-progress one-finger drag and freeze movement,
          // otherwise the object slides after finger 1 while pinching.
          try { self.canvas._currentTransform = null; } catch (err) {}
          active.lockMovementX = true;
          active.lockMovementY = true;
          e.preventDefault();
        }
      }
    }, { passive: false });

    wrapper.addEventListener('touchmove', function (e) {
      if (!isPinching || !pinchTarget || e.touches.length !== 2) return;
      // A deleted/removed object must never be resized by a stale pinch
      if (self.canvas.getObjects().indexOf(pinchTarget) === -1) { endPinch(false); return; }
      e.preventDefault();

      var dist = getDistance(e.touches);
      var rawRatio = dist / lastDist;
      // Reduce sensitivity by 50% — dampen the ratio toward 1.0
      var ratio = 1 + (rawRatio - 1) * 0.5;
      var newScaleX = initialScaleX * ratio;
      var newScaleY = initialScaleY * ratio;

      // Cap image scaling per size mode
      if (pinchTarget.type === 'image') {
        var pinchCap = self.mode.maxImgScale || 4.0;
        newScaleX = Math.min(newScaleX, pinchCap);
        newScaleY = Math.min(newScaleY, pinchCap);
      }
      // Min scale 10%
      newScaleX = Math.max(newScaleX, 0.1);
      newScaleY = Math.max(newScaleY, 0.1);

      if (pinchTarget.lockUniScaling) {
        pinchTarget.scaleX = newScaleX;
        pinchTarget.scaleY = newScaleX;
      } else {
        pinchTarget.scaleX = newScaleX;
        pinchTarget.scaleY = newScaleY;
      }
      pinchTarget.setCoords();
      self.canvas.renderAll();
    }, { passive: false });

    wrapper.addEventListener('touchend', function (e) {
      if (isPinching && e.touches.length < 2) endPinch(true);
    });

    // iOS/Android fire touchcancel (not touchend) when the system interrupts
    // a gesture — without this the stale pinch state resizes objects later.
    wrapper.addEventListener('touchcancel', function () { endPinch(false); });

  };

  // ── Switch canvas size mode without losing work ──
  // Saves the outgoing design under its own per-mode autosave slot, then either
  // restores the target mode's saved design or carries the current objects over
  // (uniformly rescaled to fit). Undo history resets — states from the two
  // sizes aren't dimensionally compatible.
  InkbondStudio.prototype.switchMode = function (modeKey, restore) {
    var self = this;
    if (!MODES[modeKey]) return;
    if (modeKey === this.mode.key) {
      if (restore) this._restoreFromSave();
      return;
    }

    // 1. Preserve the outgoing design
    this._autoSaveNow();
    var oldW = this.canvas.getWidth();
    var oldH = this.canvas.getHeight();

    // 2. Flip mode + resize the canvas
    this.mode = MODES[modeKey];
    this.canvas.discardActiveObject();
    this.activeTextObj = null;
    var size = this.getCanvasSize();
    this.canvas.setDimensions({ width: size.width, height: size.height });
    this._lastLayoutWidth = window.innerWidth;

    var saved = restore ? readSavedPayload(modeKey) : null;
    if (saved) {
      // 3a. Continue the design previously made in this mode
      this.history = [];
      this.historyIndex = -1;
      this._restoreFromSave();
    } else {
      // 3b. Carry current objects over, uniformly scaled to fit the new canvas
      var f = Math.min(size.width / oldW, size.height / oldH);
      var fx = size.width / oldW;
      var fy = size.height / oldH;
      this.canvas.getObjects().forEach(function (obj) {
        if (obj._isGuide) return;
        obj.scaleX *= f;
        obj.scaleY *= f;
        obj.left *= fx;
        obj.top *= fy;
        obj.setCoords();
      });
      this.history = [];
      this.historyIndex = -1;
      this.saveState();
    }

    this.drawGuides();
    this.canvas.renderAll();
    this._syncModeUI();
    this._syncToolbarHeight();
    this.updateHasObjects();
    this.toast('Switched to ' + this.mode.name + ' (' + this.mode.label + ')');
  };

  // ── Initialize ──
  // The studio boots from the size-selection start screen, NOT on page load:
  // window.ibStudioStart(modeKey, restore) is called when the user picks a
  // size (or the "continue your design" card). All Alpine handlers already
  // guard on window.inkbondStudio, so nothing runs before that.
  waitForFabric(function () {
    window.ibStudioStart = function (modeKey, restore) {
      if (window.inkbondStudio) {
        window.inkbondStudio.switchMode(modeKey, restore);
        return;
      }
      // Wait a frame so Alpine has revealed the editor body — Fabric measures
      // .ib-studio__canvas-area, which is display:none while the start screen
      // is up and would yield a zero-size canvas.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          window.inkbondStudio = new InkbondStudio({ mode: modeKey, restore: restore });
        });
      });
    };
    // (The start screen reads saved-design info itself via an inline script —
    // window.__ibSaved — so it works regardless of script load order.)
  });

})();
